import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Footer,
  Heading,
  Layer,
  ResponsiveContext,
  Spinner,
  Text,
} from "grommet";
import { AuthContext } from "../../contexts/AuthContext"; // Import AuthContext
import API_URL from "../../config/config";
import axios from "../../config/adminAxios";
import { useLocation } from "react-router-dom";
import ContactFormModal from "../LearningPaths/ContactFormModal";

export const StickyFooter = () => {
  const [userid, setUserId] = useState("");
  const [userName, setUserName] = useState("");
  const [userCountry, setUserCountry] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const size = useContext(ResponsiveContext);
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const year = new Date().getFullYear();
  const [popup, setPopup] = useState({
    message: "",
    status: "",
    visible: false,
  });

  const handleOpenModal = () => {
    if (!user) { setShowAuthModal(true); return; }
    setShowModal(true);
  };
  const handleCloseModal = () => setShowModal(false);

  const footerLinks = [
    {
      label: "Contact",
      onClick: handleOpenModal, // Updated to directly call the function
    },
    {
      label: "Terms",
      onClick: () =>
        window.open(
          "https://education.hpe.com/ww/en/training/contactus.html",
          "_blank",
        ),
    },
    {
      label: "Privacy",
      onClick: () =>
        window.open("https://www.hpe.com/in/en/legal/privacy.html", "_blank"),
    },
    {
      label: "Feedback",
      onClick: () => {
        window.open(
          "mailto:hpeskill.advisor@hpe.com?subject=Feedback%20regarding%20HPE%20Digital%20Skill%20Advisor&body=Please%20write%20your%20feedback%20here.",
          "_blank",
        );
      },
    },
  ];

  // Form submission handler (optional additional logic)
  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    formData = { ...formData, user_id: userid };

    try {
      await axios.post(
        `${API_URL}/dashboard/request-dedicated-learning`,
        formData,
      );
      setIsLoading(false);
      setPopup({
        message:
          "Thanks for contacting HPE Education Services. One of our friendly staff will get in touch with you shortly. If the request is urgent, please contact your local HPE Education Services office",
        status: "normal",
        visible: true,
      });
      return true;
    } catch (error) {
      console.error("Error submitting the form:", error);
      setIsLoading(false);
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers["retry-after"];
        const retryAt = retryAfter ? Date.now() + parseInt(retryAfter, 10) * 1000 : null;
        return { rateLimited: true, retryAt };
      }
      setPopup({
        message: "There was an error processing your request. Please try again.",
        status: "critical",
        visible: true,
      });
      return false;
    }
  };

  const handleRateLimit = (retryAt) => {
    setShowModal(false);
    window.dispatchEvent(new CustomEvent("app:rate-limited", { detail: { retryAt } }));
  };
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const userIdValue = user.user_table_id ?? user.user_id;
          setUserId(userIdValue);

          if (userIdValue) {
            const nameResponse = await axios.get(
              `${API_URL}/user/getUserNameById/${userIdValue}`,
            );
            setUserName(nameResponse.data.name);
            const userResponse = await axios.get(
              `${API_URL}/user/getUser/${userIdValue}`,
            );
            setUserCountry(userResponse.data.user.country);
          }
        } catch (err) {
          console.error("Error fetching user details:", err);
        }
      }
    };

    fetchUserData();
  }, [user, location]);

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <>
      <Footer
        background="black"
        direction={!["xsmall", "small"].includes(size) ? "row" : "column"}
        align={!["xsmall", "small"].includes(size) ? "center" : undefined}
        pad={{ horizontal: "medium", vertical: "small" }}
        fill="horizontal"
      >
        <Box
          direction={!["xsmall", "small"].includes(size) ? "row" : "column"}
          align={!["xsmall", "small"].includes(size) ? "center" : undefined}
          gap="xsmall"
        >
          <Text size="small">
            &copy; {year} Hewlett Packard Enterprise Development LP
          </Text>
        </Box>
        <Box direction="row" align="center" justify="center" gap="xsmall">
          <Text size="xsmall">
            Best performance on a screen resolution of 1366 x 1024 pixels or
            higher
          </Text>
        </Box>
        <Box
          direction="row"
          align={!["xsmall", "small"].includes(size) ? "center" : undefined}
          gap="xsmall"
          wrap
        >
          {footerLinks.map((link) => (
            <Button
              key={link.label}
              label={link.label}
              onClick={link.onClick}
            />
          ))}
        </Box>
      </Footer>
      {showAuthModal && (
        <Layer onEsc={() => setShowAuthModal(false)} onClickOutside={() => setShowAuthModal(false)}>
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">Account Required</Heading>
            <Text>Please create an account to explore HPE Digital Skill Advisor and submit a support ticket.</Text>
            <Box direction="row" justify="end">
              <Button label="Close" primary onClick={() => setShowAuthModal(false)} />
            </Box>
          </Box>
        </Layer>
      )}
      {showModal && (
        <ContactFormModal
          username={userName}
          email={user ? user.email : ""}
          country={userCountry}
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
          onRateLimit={handleRateLimit}
        />
      )}
      {popup.visible && (
        <Layer
          onEsc={() => setPopup({ ...popup, visible: false })}
          onClickOutside={() => setPopup({ ...popup, visible: false })}
        >
          <Box pad="medium" gap="small" width="medium">
            <Heading level={3} margin="none">
              {popup.status === "normal" ? "Success" : "Error"}
            </Heading>
            <Text>{popup.message}</Text>
            <Box direction="row" gap="medium" justify="end">
              <Button
                label="Close"
                onClick={() => setPopup({ ...popup, visible: false })}
                primary
              />
            </Box>
          </Box>
        </Layer>
      )}
    </>
  );
};
